import { createReducer } from 'redux-act';
import {
  addImagesToUploadSession,
  batchUpdateAfterUploadingOfImages,
  cleanUpImagesSuccess,
  clearImages,
  clearImagesUploadSession,
  clearSelected,
  closeFullscreen,
  deleteImage,
  deleteImagesProgress,
  deleteImagesQueueComplete,
  enqueueDeleteImagesBatches,
  fetchImagesFailed,
  trimImagesFromItems,
  fetchImagesSuccess,
  handlePhotoswipeAction,
  openImageInFullscreenAction,
  pauseUploading,
  pushImagesToBatchUpdateQueue,
  removeImagesFromLocalCaches,
  resetImagesSelectionsFilter,
  restoreUploading,
  searchImage,
  selectImageSuccess,
  setIsCropped,
  setIsImageDarggingAction,
  updateFullscreenImageListAction,
  updateImageUploadSessionData
} from '../../actions';
import { DEFAULT_IMAGE } from '../../constants';
import { uniqBy } from 'lodash';

export interface IImageDuplicate {
  id: string;
  name: string;
}

export type ImageErrors = 'warning' | 'error' | 'storage_limit' | '';

export interface IFocalPoint {
  xPercent: number;
  yPercent: number;
}

export interface IFocalPoints {
  mobile?: IFocalPoint | null;
  desktop?: IFocalPoint | null;
}

export interface IImage {
  compressionStatus: string;
  deleteAt: string;
  extension: string;
  gallery_id: string;
  height: number;
  identifier: string;
  imgsFolder: string;
  mimeType: string;
  originalImageName: string;
  sizeMb: number;
  size: number;
  status: string;
  uploadedAt: string;
  createdAt: string;
  recordedAt: string;
  width: number;
  manualOrder: number;
  uploadOrder?: number;
  randomOrder?: number;
  watermarkIdentifier: string;
  isTitleImage: boolean;
  isSmallScreenTitleImage: boolean;
  _collection: string;
  _id: string;
  _user: string;
  file?: File;
  duplicateId?: string;
  error?: string;
  errorType?: ImageErrors;
  focalPoints?: IFocalPoints;
  _watermark: string;
  isIcon?: boolean;
  uploadingStatus?: string;
  stopUploading?: boolean;
  isCollectionHeader?: boolean;
  watermarkCreatedAt?: Date;
  // _ids of same-name twins (older replaced/orphan versions) that dedupeImages
  // collapsed into this entry. Bulk delete expands these so the server cleans
  // up orphan documents that the UI would otherwise hide.
  _shadowIds?: string[];
}

export interface IImageReducerState {
  items: IImage[];
  itemsToUpdate: IImage[];
  searchImage: string;
  isFetched: boolean;
  selected: string[];
  isCropped: boolean;
  isDragging: any;
  appIcon: IImage;
  resetSelectionsFilter: boolean;
  uploadSessionImages: Record<string, IImage[]>;
  uploadIsPaused: boolean;
  imageDeletionInProcess: boolean;
  deleteImagesQueueProgress: { done: number; total: number } | null;
  fullscreen: {
    isOpen: boolean;
    images: IImage[];
    startIndex: number;
  };
  fullscreenData: {
    image: IImage;
    currentImageIndex: number;
  };
}

const initialState: IImageReducerState = {
  items: [],
  itemsToUpdate: [],
  isFetched: false,
  searchImage: '',
  selected: [],
  isCropped: false,
  appIcon: DEFAULT_IMAGE,
  resetSelectionsFilter: false,
  isDragging: false,
  uploadSessionImages: {},
  uploadIsPaused: false,
  imageDeletionInProcess: false,
  deleteImagesQueueProgress: null,
  fullscreen: {
    isOpen: false,
    images: [],
    startIndex: 0
  },
  fullscreenData: {
    image: null,
    currentImageIndex: 0
  }
};

const mergeImages = (imagesA: IImage[], imagesB: IImage[]): IImage[] => {
  const incomingIds = new Set((imagesB || []).map((i) => i._id).filter(Boolean));
  const filteredCurrent = (imagesA || []).filter((i) => !i._id || !incomingIds.has(i._id));

  return uniqBy([...filteredCurrent, ...(imagesB || [])], '_id');
};

// Preserves locally-selected title flags (desktop/mobile) when backend payload
// doesn't include them for the same gallery.
const applyLocalTitleFlags = (current: IImage[], incoming: IImage[]): IImage[] => {
  const localByGallery: Record<string, { desktopId?: string; mobileId?: string }> = {};

  for (const img of current) {
    const galleryId = img && img.gallery_id;
    if (!galleryId) continue;

    if (!localByGallery[galleryId]) localByGallery[galleryId] = {};
    if (img.isTitleImage) localByGallery[galleryId].desktopId = img._id;
    if (img.isSmallScreenTitleImage) localByGallery[galleryId].mobileId = img._id;
  }

  const hasIncomingByGallery: Record<string, { desktop: boolean; mobile: boolean }> = {};

  for (const img of incoming) {
    const galleryId = img && img.gallery_id;
    if (!galleryId) continue;

    if (!hasIncomingByGallery[galleryId]) {
      hasIncomingByGallery[galleryId] = { desktop: false, mobile: false };
    }

    if (img.isTitleImage) hasIncomingByGallery[galleryId].desktop = true;
    if (img.isSmallScreenTitleImage) hasIncomingByGallery[galleryId].mobile = true;
  }

  return incoming.map((img) => {
    const galleryId = img && img.gallery_id;
    if (!galleryId) return img;

    const local = localByGallery[galleryId];
    if (!local) return img;

    const hasIncoming = hasIncomingByGallery[galleryId] || { desktop: false, mobile: false };

    const nextIsTitleImage =
      hasIncoming.desktop || !local.desktopId ? img.isTitleImage : img._id === local.desktopId;
    const nextIsSmallScreenTitleImage =
      hasIncoming.mobile || !local.mobileId
        ? img.isSmallScreenTitleImage
        : img._id === local.mobileId;

    if (
      nextIsTitleImage === img.isTitleImage &&
      nextIsSmallScreenTitleImage === img.isSmallScreenTitleImage
    ) {
      return img;
    }

    return {
      ...img,
      isTitleImage: nextIsTitleImage,
      isSmallScreenTitleImage: nextIsSmallScreenTitleImage
    };
  });
};

// TODO: Rename to the images after refactoring
export const collectionImages = createReducer<IImageReducerState>({}, initialState);
collectionImages.on(
  fetchImagesSuccess,
  (state: IImageReducerState, payload: { items?: IImage[]; appIcon?: IImage }) => ({
    ...state,
    items: payload.items ? applyLocalTitleFlags(state.items, payload.items) : state.items,
    appIcon: payload.appIcon || state.appIcon,
    imageDeletionInProcess: false,
    isFetched: true
  })
);
collectionImages.on(
  cleanUpImagesSuccess,
  (state: IImageReducerState, payload: { items?: IImage[]; appIcon?: IImage }) => ({
    ...state,
    items: payload.items ? applyLocalTitleFlags(state.items, payload.items) : state.items,
    appIcon: payload.appIcon || state.appIcon,
    isFetched: true
  })
);

collectionImages.on(
  pushImagesToBatchUpdateQueue,
  (state: IImageReducerState, payload: { itemsToUpdate?: IImage[] }) => {
    // Update the main `items` list only in chunks to avoid frequent re-renders (virtual list).
    // IMPORTANT: when lengths are equal (`delta = 0`) we must NOT treat it as a batch boundary,
    // otherwise `items` would be merged on every single update.
    const delta = payload.itemsToUpdate.length - state.items.length;
    const conditionToUpdate = delta !== 0 && delta % 30 === 0;

    let updatedItems = state.items;

    if (conditionToUpdate) {
      const merged = mergeImages(state.items, payload.itemsToUpdate);
      updatedItems = applyLocalTitleFlags(state.items, merged);
    }

    return {
      ...state,
      itemsToUpdate: [...payload.itemsToUpdate],
      items: updatedItems
    };
  }
);

collectionImages.on(batchUpdateAfterUploadingOfImages, (state: IImageReducerState) => ({
  ...state,
  items: applyLocalTitleFlags(state.items, mergeImages(state.items, state.itemsToUpdate)),
  itemsToUpdate: []
}));

collectionImages.on(searchImage, (state: IImageReducerState, payload: string) => ({
  ...state,
  searchImage: payload
}));
collectionImages.on(selectImageSuccess, (state: IImageReducerState, payload: string[]) => ({
  ...state,
  selected: payload
}));
collectionImages.on(clearSelected, (state: IImageReducerState) => ({
  ...state,
  selected: [],
  resetSelectionsFilter: true
}));
collectionImages.on(setIsCropped, (state: IImageReducerState, payload) => ({
  ...state,
  isCropped: payload
}));
collectionImages.on(resetImagesSelectionsFilter, (state: IImageReducerState, payload) => ({
  ...state,
  resetSelectionsFilter: payload
}));
collectionImages.on(fetchImagesFailed, () => initialState);
collectionImages.on(clearImages, () => initialState);

collectionImages.on(openImageInFullscreenAction, (state, { images, index }) => ({
  ...state,
  fullscreen: { ...state.fullscreen, images, isOpen: true, startIndex: index }
}));
collectionImages.on(handlePhotoswipeAction, (state, { image, index }) => ({
  ...state,
  fullscreenData: { image, currentImageIndex: index }
}));
collectionImages.on(updateFullscreenImageListAction, (state, { images }) => ({
  ...state,
  fullscreen: { ...state.fullscreen, images }
}));

collectionImages.on(closeFullscreen, (state) => ({
  ...state,
  fullscreen: { ...state.fullscreen, isOpen: false, currentImageIndex: 0, images: [] }
}));

collectionImages.on(setIsImageDarggingAction, (state, payload) => ({
  ...state,
  isDragging: payload
}));

collectionImages.on(addImagesToUploadSession, (state, payload) => {
  const newArray = [...(state.uploadSessionImages[payload.albumId] || [])];
  const resultArray = [...newArray, ...payload.images];
  const uniqueArray = uniqBy(resultArray, 'originalImageName');

  return {
    ...state,
    uploadSessionImages: {
      ...state.uploadSessionImages,
      [payload.albumId]: uniqueArray
    }
  };
});

collectionImages.on(updateImageUploadSessionData, (state, payload) => {
  const newUploadSessionArray = [...(state.uploadSessionImages[payload.albumId] || [])];
  const currentSessionIdx = newUploadSessionArray.findIndex(
    (image) => image?.originalImageName === payload.image?.originalImageName
  );

  if (payload.stopUploading) {
    currentSessionIdx >= 0 && newUploadSessionArray.splice(currentSessionIdx, 1);
  } else {
    currentSessionIdx >= 0 && newUploadSessionArray.splice(currentSessionIdx, 1, payload.image);
  }

  return {
    ...state,
    uploadSessionImages: {
      ...state.uploadSessionImages,
      [payload.albumId]: newUploadSessionArray
    }
  };
});

collectionImages.on(clearImagesUploadSession, (state) => ({
  ...state,
  uploadSessionImages: {}
}));

collectionImages.on(deleteImage, (state) => ({ ...state, imageDeletionInProcess: true }));
// Bulk flag lives on enqueue, not on the higher-level deleteImages action —
// the saga early-returns for empty selection and would otherwise leave the
// spinner stuck on.
collectionImages.on(enqueueDeleteImagesBatches, (state, payload) => ({
  ...state,
  imageDeletionInProcess: true,
  deleteImagesQueueProgress: { done: 0, total: payload.imageIDs.length }
}));
collectionImages.on(deleteImagesProgress, (state, payload) => ({
  ...state,
  deleteImagesQueueProgress: payload
}));
collectionImages.on(deleteImagesQueueComplete, (state) => ({
  ...state,
  imageDeletionInProcess: false,
  deleteImagesQueueProgress: null
}));
collectionImages.on(trimImagesFromItems, (state, payload) => {
  const ids = new Set(payload.imageIds || []);
  if (!ids.size) return state;

  return {
    ...state,
    items: state.items.filter((img: IImage) => !ids.has(img?._id))
  };
});

collectionImages.on(removeImagesFromLocalCaches, (state: IImageReducerState, payload) => {
  const ids = new Set(payload.imageIds || []);
  const names = new Set((payload.originalImageNames || []).filter(Boolean));

  const shouldRemove = (image: IImage) =>
    ids.has(image?._id) || (image?.originalImageName ? names.has(image.originalImageName) : false);

  const nextItemsToUpdate = state.itemsToUpdate.filter((img) => !shouldRemove(img));

  const nextUploadSessionImages = Object.keys(state.uploadSessionImages).reduce((acc, albumId) => {
    acc[albumId] = (state.uploadSessionImages[albumId] || []).filter((img) => !shouldRemove(img));

    return acc;
  }, {} as Record<string, IImage[]>);

  const nextFullscreenImages = (state.fullscreen.images || []).filter((img) => !shouldRemove(img));
  const fullscreenDataImage = state.fullscreenData?.image;
  const nextFullscreenDataImage =
    fullscreenDataImage && shouldRemove(fullscreenDataImage) ? null : fullscreenDataImage;

  return {
    ...state,
    itemsToUpdate: nextItemsToUpdate,
    uploadSessionImages: nextUploadSessionImages,
    fullscreen: { ...state.fullscreen, images: nextFullscreenImages },
    fullscreenData: { ...state.fullscreenData, image: nextFullscreenDataImage },
    selected: state.selected.filter((id) => !ids.has(id))
  };
});

collectionImages.on(pauseUploading, (state) => ({ ...state, uploadIsPaused: true }));
collectionImages.on(restoreUploading, (state) => ({ ...state, uploadIsPaused: false }));
